import type { Logger } from "@expert/logging";
import type { GaiaPayloadConfigs, OrchestratorInvokeMessagePayload } from "@expert/shared-types";

import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { sendMetrics } from "@expert/cloudwatch-metrics";
import { type TimelineFeatures, useFeatures } from "@expert/features";
import {
    type GaiaWebSocketResponse,
    fallbackGaiaPayloadProps,
    gaiaWsEventBus,
    getExperimentName,
    getFallbackPartner,
    getSupportedLineOfBusiness,
    useGaiaStore,
    useGaiaWebsocket,
} from "@expert/gaia";
import { trackFullStoryEvent } from "@expert/monitoring";
import { useMemo } from "react";

import type { RetryPayload, SendMessageProps } from "../shared-types";

import { addMessage, getPartnerMetricDimension, sendFailureMetric, useTimelineStore } from "../state";

const ORCHESTRATOR_RESPONSE_TIMEOUT_MS = 20_000;

export function setupOrchestratorTimeout(
    correlationId: string,
    sessionId: string,
    logger: Logger,
    expertMessageId: string,
    retryData: RetryPayload,
    appName?: string,
    startTime?: number,
): () => void {
    let cleanedUp = false;

    const emitRoundTripDuration = (outcome: "success" | "error" | "timeout") => {
        if (startTime === undefined || appName === undefined) {
            return;
        }
        void sendMetrics([
            {
                metrics: [{ name: "Duration", value: Math.round(performance.now() - startTime), unit: "ms" }],
                dimensions: {
                    actionName: "gaia.orchestrator_invoke_response",
                    gaiaAppName: appName,
                    outcome,
                    ...getPartnerMetricDimension(sessionId),
                },
            },
        ]);
    };

    const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        unsubSuccess();
        unsubError();
        clearTimeout(timer);
    };

    const unsubSuccess = gaiaWsEventBus.on("gaia_ws_orchestrator-invoke-success", (response: GaiaWebSocketResponse) => {
        if (response.correlationId !== correlationId) return;
        emitRoundTripDuration("success");
        cleanup();
        useTimelineStore.getState().updateMessage(sessionId, expertMessageId, {
            metadata: { retryPayload: undefined },
        });
    });

    const unsubError = gaiaWsEventBus.on("gaia_ws_orchestrator-invoke-error", (response: GaiaWebSocketResponse) => {
        if (response.correlationId !== correlationId) return;
        emitRoundTripDuration("error");
        cleanup();
        // Surface the failure inline on the expert's message (the RetryButton), the same way the timeout
        // path does. We key off correlationId rather than the error body because the error payload shape
        // varies and may carry no message id (e.g. `{ message, trackingId }`), so this is the only place
        // that reliably knows which message to mark.
        sendFailureMetric("botFailure", sessionId);
        useTimelineStore.getState().updateMessage(sessionId, expertMessageId, {
            metadata: { retryPayload: retryData },
        });
    });

    const timer = setTimeout(() => {
        emitRoundTripDuration("timeout");
        cleanup();
        logger.warn({ correlationId, sessionId }, "GAIA orchestrator response timed out");
        sendFailureMetric("botFailure", sessionId);
        useTimelineStore.getState().updateMessage(sessionId, expertMessageId, {
            metadata: { retryPayload: retryData },
        });
    }, ORCHESTRATOR_RESPONSE_TIMEOUT_MS);

    return cleanup;
}

export const useSendExpertAssistMessage = (loggerProp: Logger, gaiaPayloadProps: GaiaPayloadConfigs) => {
    const websocketObj = useGaiaWebsocket();
    const { subscriptionStatus } = useGaiaStore();
    const { dispatcher } = useReactAnalytics();
    const {
        features: { isGroundedSearchExperimentEnabled, isSalesFAQEnabled, isProactiveGuidanceEnabled },
    } = useFeatures<TimelineFeatures>();
    const { expertId, currentSessionId: sessionId, getTimelineState } = useTimelineStore();
    const { callSid, chatId, lob, partner } = getTimelineState(sessionId);

    const logger = useMemo(() => loggerProp.child({ module: "useSendExpertAssistMessage" }), [loggerProp]);

    const sendMessage = async ({ id, metadata, text, type, source }: SendMessageProps) => {
        const messageId = id ?? crypto.randomUUID();
        const correlationId = crypto.randomUUID();
        const formattedPartnerForGaia = getFallbackPartner({ partner });
        const { areExperimentsEnabled, appName } =
            gaiaPayloadProps ?? fallbackGaiaPayloadProps(chatId ? "chat" : "voice");
        // Route expert-initiated Search messages through the reactive-guidance app only when the live
        // connection is actually Simplr. It is unsupported on the legacy GAIA flow, so gate on the
        // resolved transport (`isSimplrLive`) rather than the raw `isSimplrWebsocketLiveEnabled` flag:
        // the flag can be on while the call still resolves to GAIA (no Simplr URL in this env, partner
        // not in the prod cutover allowlist, or a standalone expert-assist connection), and swapping the
        // appName in that case would send an unsupported app over GAIA and break the search.
        const isSimplrLive = websocketObj?.isSimplrLive ?? false;
        const resolvedAppName =
            isProactiveGuidanceEnabled && isSimplrLive ? "expert-assist-reactive-guidance" : appName;
        const experimentName = areExperimentsEnabled
            ? getExperimentName({
                  isGroundedSearchExperimentEnabled,
                  isSalesFAQEnabled,
                  lob,
              })
            : undefined;

        const lineOfBusiness = getSupportedLineOfBusiness({ partner: formattedPartnerForGaia, lob });
        const payload: OrchestratorInvokeMessagePayload = {
            action: "orchestrator-invoke",
            appName: resolvedAppName,
            message: text,
            messageId,
            messageType: "Search",
            partner: formattedPartnerForGaia,
            sessionId: chatId ?? callSid ?? sessionId,
            sessionGroupId: sessionId,
            userId: expertId,
            userType: "expert",
            correlationId,
            experimentName,
            lineOfBusiness,
        };

        const messageText = metadata?.helpMeNowText ?? text;
        addMessage(sessionId, {
            id: messageId,
            text: messageText,
            type,
            isUnread: false,
            metadata,
            status: "active",
        });

        const retryData: RetryPayload = {
            text,
            type,
            source,
            metadata,
        };

        const analyticsInfo = {
            expertAssistAppName: resolvedAppName,
            expertAssistExperimentName: experimentName,
            partner,
            messageId,
            messageType: type,
            source,
        };

        if (!websocketObj) {
            logger.error({ payload, websocketObj }, "There is no websocket obj");
            sendFailureMetric("connectionFailure", sessionId);
            useTimelineStore.getState().updateMessage(sessionId, messageId, {
                metadata: { retryPayload: retryData },
            });
            return;
        }

        if (subscriptionStatus[sessionId] !== "subscribed") {
            logger.warn("Expert session not subscribed, subscribing before sending message");
            try {
                await websocketObj.subscribeSessionToGaia({
                    sessionId,
                    chatId,
                    callSid,
                    partner: formattedPartnerForGaia,
                    sendJsonMessage: websocketObj.sendJsonMessage,
                    logger,
                });
            } catch (error) {
                logger.error({ error }, "Failed to subscribe session to GAIA before sending message");
                sendFailureMetric("connectionFailure", sessionId);
                useTimelineStore.getState().updateMessage(sessionId, messageId, {
                    metadata: { retryPayload: retryData },
                });
                return;
            }
        }
        const startTime = performance.now();
        websocketObj.sendJsonMessage(payload);
        setupOrchestratorTimeout(correlationId, sessionId, logger, messageId, retryData, resolvedAppName, startTime);
        void dispatcher.dispatchBusinessEvent("MessageSent", {
            senderType: "expert",
            ...analyticsInfo,
        });
        logger.info({ payload }, "Message sent to gaia");
        trackFullStoryEvent("ExpertAssistMessageSent", {
            sessionId,
            callSid,
            chatId,
            ...analyticsInfo,
        });
    };
    return sendMessage;
};
